import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, CalendarDays, ListChecks } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { TASK_DEPARTMENTS, TASK_MEMBERS, TASK_STATUSES } from "./tasksConstants";
import {
  calendarDaysUntilDue,
  endExclusiveFromStart,
  formatDeadlineBadge,
  formatTaskCode,
  isTaskDeadlineUrgent,
  startOfLocalMonth,
  startOfLocalToday,
  startOfLocalWeek,
} from "./tasksUi";
import { KpiCard, MiniTable, PercentPills, SectionHeader, StackedBar } from "./TasksDashboardWidgets";

type Period = "week" | "month";

type TaskRow = {
  id: string;
  task_number: number;
  title: string;
  assignees: string[];
  department: string;
  priority: "normal" | "high";
  status: (typeof TASK_STATUSES)[number];
  created_at: string;
  updated_at: string;
  due_date: string | null;
  created_by_email: string | null;
};

function normalize(s: string) {
  return s.trim().toLowerCase();
}

function fmtDateLabel(d: Date) {
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function TasksDashboardPage() {
  const [period, setPeriod] = useState<Period>("week");
  const [department, setDepartment] = useState<string>("all");
  const [assignee, setAssignee] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<TaskRow[]>([]);

  const today = useMemo(() => startOfLocalToday(), []);
  const periodStart = useMemo(() => (period === "week" ? startOfLocalWeek(today) : startOfLocalMonth(today)), [period, today]);
  const periodEndExclusive = useMemo(() => endExclusiveFromStart(periodStart, period), [periodStart, period]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("tasks")
        .select("id,task_number,title,assignees,department,priority,status,created_at,updated_at,due_date,created_by_email")
        .order("updated_at", { ascending: false });

      if (cancelled) return;
      if (error) {
        setError(error.message);
        setTasks([]);
      } else {
        setTasks((data ?? []) as TaskRow[]);
      }
      setLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (department !== "all" && normalize(t.department) !== normalize(department)) return false;
      if (assignee === "unassigned") {
        if ((t.assignees?.length ?? 0) > 0) return false;
      } else if (assignee !== "all") {
        if (!t.assignees?.includes(assignee)) return false;
      }
      return true;
    });
  }, [tasks, department, assignee]);

  const active = useMemo(
    () => filtered.filter((t) => t.status !== "done" && t.status !== "cancelled"),
    [filtered]
  );

  const overdue = useMemo(() => active.filter((t) => (calendarDaysUntilDue(t.due_date) ?? 0) < 0), [active]);

  const dueSoon = useMemo(
    () =>
      active.filter((t) => {
        const d = calendarDaysUntilDue(t.due_date);
        return d !== null && d >= 0 && d <= 5;
      }),
    [active]
  );

  const urgentDeadlines = useMemo(() => {
    const list = active.filter((t) => isTaskDeadlineUrgent(t.due_date, t.status, 5));
    return [...list]
      .map((t) => ({ t, days: calendarDaysUntilDue(t.due_date) ?? 9999 }))
      .sort((a, b) => a.days - b.days)
      .slice(0, 12);
  }, [active]);

  const inPeriodCreated = useMemo(
    () => filtered.filter((t) => new Date(t.created_at).getTime() >= periodStart.getTime() && new Date(t.created_at).getTime() < periodEndExclusive.getTime()),
    [filtered, periodStart, periodEndExclusive]
  );

  const inPeriodCompleted = useMemo(
    () =>
      filtered.filter(
        (t) =>
          t.status === "done" &&
          new Date(t.updated_at).getTime() >= periodStart.getTime() &&
          new Date(t.updated_at).getTime() < periodEndExclusive.getTime()
      ),
    [filtered, periodStart, periodEndExclusive]
  );

  const highOpen = useMemo(() => active.filter((t) => t.priority === "high"), [active]);

  const statusSegments = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of TASK_STATUSES) counts.set(s, 0);
    for (const t of filtered) counts.set(t.status, (counts.get(t.status) ?? 0) + 1);
    return [
      { label: "to-do", value: counts.get("to-do") ?? 0, className: "bg-slate-400" },
      { label: "progress", value: counts.get("progress") ?? 0, className: "bg-sky-500" },
      { label: "done", value: counts.get("done") ?? 0, className: "bg-emerald-500" },
      { label: "cancelled", value: counts.get("cancelled") ?? 0, className: "bg-rose-500" },
    ];
  }, [filtered]);

  const deptSegments = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of TASK_DEPARTMENTS) counts.set(d, 0);
    for (const t of active) {
      const key = TASK_DEPARTMENTS.find((d) => normalize(d) === normalize(t.department)) ?? t.department;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return (TASK_DEPARTMENTS as readonly string[]).map((d, i) => ({
      label: d,
      value: counts.get(d) ?? 0,
      className: ["bg-slate-300", "bg-amber-300", "bg-sky-300", "bg-violet-300", "bg-emerald-300", "bg-rose-300", "bg-teal-300"][i % 7],
    }));
  }, [active]);

  const assigneeSegments = useMemo(() => {
    const counts = new Map<string, number>();
    counts.set("Unassigned", 0);
    for (const m of TASK_MEMBERS) counts.set(m, 0);
    for (const t of active) {
      if ((t.assignees?.length ?? 0) === 0) {
        counts.set("Unassigned", (counts.get("Unassigned") ?? 0) + 1);
      } else {
        for (const a of t.assignees) counts.set(a, (counts.get(a) ?? 0) + 1);
      }
    }
    return ["Unassigned", ...TASK_MEMBERS]
      .map((k) => ({ label: k, value: counts.get(k) ?? 0 }))
      .filter((x) => x.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [active]);

  const periodLabel = useMemo(() => {
    const left = fmtDateLabel(periodStart);
    const right = fmtDateLabel(new Date(periodEndExclusive.getTime() - 1));
    return `${left} → ${right}`;
  }, [periodStart, periodEndExclusive]);

  return (
    <div className="min-h-screen bg-brand-gray text-brand-blue">
      <div className="mx-auto max-w-6xl px-4 py-6">
        <SectionHeader
          title="Tasks Dashboard"
          right={
            <div className="flex items-center gap-2">
              <Link
                to="/tasks"
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-brand-blue shadow-sm ring-1 ring-black/10 hover:bg-brand-gray/20"
              >
                <ListChecks className="h-4 w-4" />
                Tasks
              </Link>
            </div>
          }
        />

        <div className="mt-3 flex flex-col gap-2 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-black/10 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPeriod("week")}
              className={[
                "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide ring-1 transition-colors",
                period === "week"
                  ? "bg-brand-blue/10 text-brand-blue ring-brand-blue/40 ring-2 shadow-sm"
                  : "bg-white text-brand-blue/75 ring-black/15 hover:bg-brand-gray/20",
              ].join(" ")}
            >
              <CalendarDays className="h-4 w-4" />
              Weekly
            </button>
            <button
              type="button"
              onClick={() => setPeriod("month")}
              className={[
                "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide ring-1 transition-colors",
                period === "month"
                  ? "bg-brand-blue/10 text-brand-blue ring-brand-blue/40 ring-2 shadow-sm"
                  : "bg-white text-brand-blue/75 ring-black/15 hover:bg-brand-gray/20",
              ].join(" ")}
            >
              <BarChart3 className="h-4 w-4" />
              Monthly
            </button>

            <span className="ml-1 text-xs font-semibold text-brand-blue/55">{periodLabel}</span>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="rounded-xl bg-brand-gray/40 px-3 py-2 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
            >
              <option value="all">All departments</option>
              {TASK_DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>

            <select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              className="rounded-xl bg-brand-gray/40 px-3 py-2 text-sm outline-none ring-2 ring-transparent focus:ring-brand-maroon"
            >
              <option value="all">All assignees</option>
              <option value="unassigned">Unassigned</option>
              {TASK_MEMBERS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error ? (
          <p className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 shadow-sm ring-1 ring-red-700/10">
            {error}
          </p>
        ) : null}

        {loading ? (
          <div className="mt-6 rounded-2xl bg-white p-8 text-center text-sm text-brand-blue/70 shadow-sm ring-1 ring-black/10">
            Loading…
          </div>
        ) : (
          <>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <KpiCard label="Active tasks" value={active.length} hint="Open workload right now" />
              <KpiCard label="Overdue" value={overdue.length} tone={overdue.length ? "danger" : "good"} hint="Due date passed" />
              <KpiCard label="Due in 5 days" value={dueSoon.length} tone={dueSoon.length ? "warn" : "good"} hint="Upcoming deadlines" />
              <KpiCard label="Completed (period)" value={inPeriodCompleted.length} tone="good" hint={`Using updated_at in ${period}`} />
              <KpiCard label="High priority open" value={highOpen.length} tone={highOpen.length ? "warn" : "neutral"} hint="High priority only" />
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-3">
              <StackedBar title="Status distribution" subtitle="All tasks (filtered)" segments={statusSegments} />

              <PercentPills
                title="Period activity"
                items={[
                  { label: "Created", value: inPeriodCreated.length, tone: "bg-sky-50 text-sky-950 ring-sky-200/80" },
                  { label: "Completed", value: inPeriodCompleted.length, tone: "bg-emerald-50 text-emerald-950 ring-emerald-200/80" },
                ]}
              />

              <StackedBar title="Department workload" subtitle="Open tasks (filtered)" segments={deptSegments} />
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/10">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-bold">Assignee workload (top)</p>
                  <p className="text-xs font-bold text-brand-blue/50">{active.length}</p>
                </div>
                <div className="mt-3 space-y-2">
                  {assigneeSegments.length === 0 ? (
                    <p className="text-sm text-brand-blue/60">No active tasks.</p>
                  ) : (
                    assigneeSegments.map((r) => {
                      const pct = active.length ? Math.round((r.value / active.length) * 100) : 0;
                      return (
                        <div key={r.label} className="flex items-center gap-3">
                          <p className="w-36 truncate text-xs font-semibold text-brand-blue/70 sm:w-44">{r.label}</p>
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-brand-gray/40 ring-1 ring-black/5">
                            <div className="h-full bg-brand-maroon/80" style={{ width: `${pct}%` }} />
                          </div>
                          <p className="w-12 text-right text-xs font-bold text-brand-blue/55">{r.value}</p>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <MiniTable
                title="Urgent deadlines"
                subtitle="Overdue + due within 5 days"
                footerHref="/tasks"
                rows={
                  urgentDeadlines.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-brand-blue/60">No urgent deadlines.</div>
                  ) : (
                    urgentDeadlines.map(({ t, days }) => (
                      <Link
                        key={t.id}
                        to={`/tasks/${formatTaskCode(t.task_number)}`}
                        className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-brand-gray/15"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-brand-blue">{t.title}</p>
                          <p className="mt-0.5 truncate text-xs font-semibold text-brand-blue/55">
                            {formatTaskCode(t.task_number)} · {t.department} · {(t.assignees?.length ?? 0) ? t.assignees.join(", ") : "Unassigned"}
                          </p>
                        </div>
                        <span
                          className={[
                            "shrink-0 rounded-full px-2 py-1 text-[11px] font-bold ring-1",
                            days < 0
                              ? "bg-rose-50 text-rose-950 ring-rose-200/80"
                              : days === 0
                                ? "bg-amber-50 text-amber-950 ring-amber-200/80"
                                : "bg-sky-50 text-sky-950 ring-sky-200/80",
                          ].join(" ")}
                          title={formatDeadlineBadge(days)}
                        >
                          {formatDeadlineBadge(days)}
                        </span>
                      </Link>
                    ))
                  )
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

